import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import DashboardLayout from '@/components/layout/dashboard-layout';
import DashboardAlerts from '@/components/dashboard/dashboard-alerts';
import DashboardFilters from '@/components/dashboard/dashboard-filters';
import DashboardKpiCards from '@/components/dashboard/dashboard-kpi-cards';
import OperationsPipeline from '@/components/dashboard/operations-pipeline';
import SalesTrendChart from '@/components/dashboard/sales-trend-chart';
import { useAuth } from '@/contexts/auth-context';
import { getDashboardData } from '@/services/dashboard.service';
import {
  AlertResult,
  DashboardFilterState,
  DashboardOverviewResult,
  PipelineResult,
  SalesTrendResult,
} from '@/types/dashboard';

interface DashboardState {
  overview: DashboardOverviewResult;
  salesTrend: SalesTrendResult;
  pipeline: PipelineResult;
  alerts: AlertResult;
}

export default function CentralDashboardPage() {
  const { user, hasPermission } = useAuth();
  const hasFinancialAccess = Boolean(
    user?.roleName === 'SUPER_ADMIN' || hasPermission('profitability.view'),
  );

  const [filters, setFilters] = useState<DashboardFilterState>(() => {
    const today = new Date();
    const toStr = today.toISOString().split('T')[0];
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const fromStr = firstDay.toISOString().split('T')[0];
    return {
      marketplace: 'ALL',
      dateFrom: fromStr,
      dateTo: toStr,
    };
  });

  const [data, setData] = useState<DashboardState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getDashboardData(filters);
      setData(result);
    } catch (err: any) {
      // Clean display of error without raw token stack
      const msg = err?.message || 'Failed to load central dashboard data. Please refresh.';
      setError(msg.includes('401') || msg.toLowerCase().includes('session') ? 'Session refreshed. Click Retry to reload.' : msg);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header with Welcome & Live Channel Indicators */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-bold text-gray-900">
                Central Operations Dashboard
              </h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Live Sync Active
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Real-time multi-channel order processing, warehouse fulfillment, returns QC, and profit reconciliation.
            </p>
          </div>

          {/* Quick Navigation Shortcuts */}
          <div className="flex flex-wrap items-center gap-2">
            <Link
              href="/orders"
              className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 shadow-xs hover:bg-gray-50 transition-colors"
            >
              Orders
            </Link>
            <Link
              href="/inventory"
              className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 shadow-xs hover:bg-gray-50 transition-colors"
            >
              Inventory
            </Link>
            <Link
              href="/marketplaces/flipkart/returns"
              className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 border border-blue-200 shadow-xs hover:bg-blue-100 transition-colors"
            >
              Flipkart Returns Hub
            </Link>
            <Link
              href="/inventory/ims-transfers"
              className="rounded-lg bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 border border-purple-200 shadow-xs hover:bg-purple-100 transition-colors"
            >
              Central IMS Sync
            </Link>
            {hasFinancialAccess && (
              <Link
                href="/profit-loss"
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 shadow-xs hover:bg-gray-50 transition-colors"
              >
                Profit &amp; Loss
              </Link>
            )}
            <Link
              href="/reports"
              className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-indigo-700 transition-colors"
            >
              Reports Hub ↗
            </Link>
          </div>
        </div>

        {/* Quick Action Launcher Station */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
              <span>🚀</span>
              <span>Quick Action Command Station</span>
            </h2>
            <span className="text-[11px] text-gray-400">1-Click operational shortcuts</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Link
              href="/marketplaces/flipkart/returns"
              className="group flex flex-col items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-blue-50/60 hover:border-blue-200 transition-all"
            >
              <span className="text-lg mb-1 group-hover:scale-110 transition-transform">⚡</span>
              <span className="text-xs font-bold text-gray-900 group-hover:text-blue-700">Rapid Gate Intake</span>
              <span className="text-[10px] text-gray-400 mt-0.5">Scan inbound AWB</span>
            </Link>

            <Link
              href="/marketplaces/flipkart/packing"
              className="group flex flex-col items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-amber-50/60 hover:border-amber-200 transition-all"
            >
              <span className="text-lg mb-1 group-hover:scale-110 transition-transform">📦</span>
              <span className="text-xs font-bold text-gray-900 group-hover:text-amber-700">Packing Station</span>
              <span className="text-[10px] text-gray-400 mt-0.5">Pack &amp; print labels</span>
            </Link>

            <Link
              href="/inventory/pools"
              className="group flex flex-col items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-purple-50/60 hover:border-purple-200 transition-all"
            >
              <span className="text-lg mb-1 group-hover:scale-110 transition-transform">🎯</span>
              <span className="text-xs font-bold text-gray-900 group-hover:text-purple-700">Channel Pools</span>
              <span className="text-[10px] text-gray-400 mt-0.5">Allocate buffers</span>
            </Link>

            <Link
              href="/inventory/movements"
              className="group flex flex-col items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-emerald-50/60 hover:border-emerald-200 transition-all"
            >
              <span className="text-lg mb-1 group-hover:scale-110 transition-transform">📊</span>
              <span className="text-xs font-bold text-gray-900 group-hover:text-emerald-700">Stock Movements</span>
              <span className="text-[10px] text-gray-400 mt-0.5">Audit log history</span>
            </Link>

            <Link
              href="/marketplaces/flipkart/settlements"
              className="group flex flex-col items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-indigo-50/60 hover:border-indigo-200 transition-all"
            >
              <span className="text-lg mb-1 group-hover:scale-110 transition-transform">💰</span>
              <span className="text-xs font-bold text-gray-900 group-hover:text-indigo-700">Settlements Hub</span>
              <span className="text-[10px] text-gray-400 mt-0.5">Reconcile payouts</span>
            </Link>

            <Link
              href="/inventory/ims-transfers"
              className="group flex flex-col items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-teal-50/60 hover:border-teal-200 transition-all"
            >
              <span className="text-lg mb-1 group-hover:scale-110 transition-transform">🌐</span>
              <span className="text-xs font-bold text-gray-900 group-hover:text-teal-700">IMS Master Sync</span>
              <span className="text-[10px] text-gray-400 mt-0.5">Port 8001 transfers</span>
            </Link>
          </div>
        </div>

        {/* Filters Bar */}
        <DashboardFilters
          filters={filters}
          onFilterChange={setFilters}
          onRefresh={loadDashboard}
          isLoading={isLoading}
        />

        {/* Error State with Retry Button */}
        {error && (
          <div className="rounded-xl bg-amber-50 p-4 border border-amber-200 text-amber-900 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-3">
              <span className="text-lg">ℹ️</span>
              <div>
                <p className="text-sm font-semibold">{error}</p>
                <p className="text-xs text-amber-700 mt-0.5">Click retry to fetch the freshest multi-channel metrics.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={loadDashboard}
              className="rounded-lg bg-amber-700 px-4 py-1.5 text-xs font-bold text-white hover:bg-amber-800 transition-colors shadow-xs"
            >
              Refresh Data
            </button>
          </div>
        )}

        {/* Loading State Skeleton */}
        {isLoading && !data && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-28 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
            <div className="h-72 rounded-xl bg-gray-100 animate-pulse" />
          </div>
        )}

        {/* Dashboard Main Content */}
        {data && (
          <div className="space-y-6">
            {/* Top Operational and Financial KPI Cards */}
            <DashboardKpiCards
              overview={data.overview}
              hasFinancialAccess={hasFinancialAccess}
            />

            {/* Sales Trend Chart & Operational Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <SalesTrendChart
                  trend={data.salesTrend}
                  hasFinancialAccess={hasFinancialAccess}
                />
              </div>
              <div className="lg:col-span-1">
                <DashboardAlerts alerts={data.alerts} />
              </div>
            </div>

            {/* Operations Pipelines (Orders Fulfillment & Returns QC) */}
            <OperationsPipeline pipeline={data.pipeline} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
